/**
 * Note that we do not have to handle discount computations here.
 * The variants selected for the recharge products already have their prices 
 * adjusted accordingly
 */

document.addEventListener('alpine:init', () => {
  const assertDependenciesInstalled = () => {
    if (!window.routes?.cart_add_url) {
      throw new Error("Routes must be configured in the theme layout.");
    }

    if (!getScriptJSONOrFail("data-cart-json")) {
      throw new Error("Cart data must be configured in the theme layout.");
    }
  };

  const displayCartHeader = () => {
    const header = Alpine.store("header");
    if (header && !header.isCartVisorVisible) {        
      setTimeout(() => Alpine.store("header").openCartVisor(), 100);
    }
  };

  assertDependenciesInstalled();

  Alpine.store("cart", {
    mostRecentItemAdded: null,
    addToCartErrorMessage: null,
    isAddingProduct: false,
    mostRecentItemsAdded: [],
    lineItemIdsChanging: new Set(),
    shippingCost: 0,
    taxes: 0,
    ...getScriptJSONOrFail("data-cart-json"),
    init() {
      this.clearRecentlyAddedItems();
      this.debouncedUpdateLineItemQuantities = window.debounce((updates) => {
        this.updateLineItemQuantities(updates).then(() => {
          for (const key in updates) {
            this.lineItemIdsChanging.delete(parseInt(key));
          }
        });
      }, 1000, this);
      // Recompute shipping and total when location changes (e.g. from modal)
      window.addEventListener('shippingLocationUpdated', () => {
        if (Alpine.store("spendProgress")) {
          this.applyComputedValues();
        }
      });
    },
    async changeLineItemQuantity(item, quantity) {
      item.quantity = quantity;
      this.lineItemIdsChanging.add(item.variant_id);
      this.debouncedUpdateLineItemQuantities({ [item.variant_id]: quantity });
    },
    money(number) { // to pattern to money liquid filter
      return window.toMoneyFormat(number / 100);
    },
    async sendJsonRequest(url, body, configs = {}) {
      const payload = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': `application/json` },
        ...configs, // overwrites
        body: JSON.stringify(body),
      };

      const response = await fetch(url, payload);
      const responseJson = await response.json();

      if (!response.ok) {
        throw {
          ...responseJson,
          error: new Error(responseJson.message)
        };
      }
      return responseJson;
    },
    async quickAddToCart(id) {
      const quantity = 1;
      const item = { id, quantity };
      return await this.addLineItems([item]);
    },
    async addLineItems(items) {
      this.isAddingProduct = true;      
      this.clearRecentlyAddedItems();
      displayCartHeader();

      let refreshedCart = null;
      try {
        // https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-add-js 
        await this.sendJsonRequest(`${routes.cart_add_url}.js`, { items });

        // Updates from adding line items is not consistent with the initial state
        // so let's get and refresh cart instead of direcly applying the updates
        refreshedCart = await this.getAndRefreshCart();

        this.displayMostRecentItemAdded(items, refreshedCart);
      } catch (e) {
        this.addToCartErrorMessage = e.description;
        console.error({ addToCartError: e });
        //  Try to display the items anyway in case they have enough details
        this.displayMostRecentItemAdded(items, { items });
      } finally {
        this.isAddingProduct = false;       
      }

      return refreshedCart;
    },
    displayMostRecentItemAdded(items, refreshedCart) {
      this.mostRecentItemsAdded = items.map(addedItem => {
        let cartItem = refreshedCart?.items.find(i => i.id == addedItem.id);
        const hasEnoughDetails = addedItem.price && addedItem.product_title
        if (!cartItem && hasEnoughDetails) {
          cartItem = addedItem
        }

        console.log({ refreshedCart, cartItem });

        // There's a possibility that the customer has already added the same item in the cart.
        // In those case scenario, if we don't use `addedItem.quantity`, it will show the current
        // quantity of the items in the cart instead of the actual number of added items.
        return {
          ...cartItem,
          quantity: addedItem.quantity
        }
      });

      // Show the visor if it's here
      if (Alpine.store("header")) {
        this.mostRecentItemAdded = this.mostRecentItemsAdded[0];
        const { quantity } = this.mostRecentItemAdded;
        const itemsPluralized = quantity > 1 ? "items" : "item"
        this.mostRecentItemAdded.display_message = `${quantity} ${itemsPluralized} added to your cart`
        this.mostRecentItemAdded.display_price = this.money(this.mostRecentItemAdded.price);
        this.mostRecentItemAdded.display_line_price = this.money(this.mostRecentItemAdded.line_price);
        Alpine.store("header").startDelayedClosing();
      }
    },
    async updateLineItemQuantities(updates) {
      // https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-update-js
      const cartUpdates = await this.sendJsonRequest(`${routes.cart_update_url}.js`, { updates });
      return this.applyUpdates(cartUpdates);
    },
    async deleteLineItems(lineItemIdOrKeys) {
      const updates = {};
      for (const idOrKey of lineItemIdOrKeys) {
        updates[idOrKey] = 0;
      }
      return this.updateLineItemQuantities(updates);
    },
    async getAndRefreshCart() {
      // https://shopify.dev/docs/api/ajax/reference/cart#get-locale-cart-js      
      const response = await fetch(`/cart.js`);
      const cartUpdates = await response.json();
      return this.applyUpdates(cartUpdates);
    },
    clearRecentlyAddedItems() {
      this.mostRecentItemsAdded = [];
      this.mostRecentItemAdded = {
        featured_image: { url: "", alt: "" },
        properties: { shipping_interval_frequency: false },
        display_message: "",
        display_price: "",
        display_line_price: "",
        product_title: ""
      };
    },
    applyComputedValues() {
      if (Alpine.store("spendProgress")) {
        const spendProgress = Alpine.store("spendProgress");
        // Only include shipping in total when a location is set; otherwise 0 so total = subtotal
        const shippingCents = spendProgress.hasShippingLocation && spendProgress.currentShippingCost != null
          ? spendProgress.currentShippingCost * 100
          : 0;
        this.shippingCost = shippingCents;
        this.taxes = (this.total_price + shippingCents) / 11; // 10% of the cost
      }
    },
    applyUpdates(cartUpdates) {
      // https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-change-js     
      for (const key in cartUpdates) {
        this[key] = cartUpdates[key];
      }

      // Update dependent alpine stores if they exist
      if (Alpine.store("spendProgress")) {
        Alpine.store("spendProgress").totalPrice = this.total_price;
      }

      this.applyComputedValues();

      return this;
    },
    hasOnlyNonRefrigeratedProducts() {
      return this.items.every(i => `${i.sku}`.endsWith("-NR"));
    }
  });
});